/**
 * Utility for creating bulk download zip files from uploaded files
 */

import JSZip from "jszip";

// Configuration for connection pooling
const CONCURRENT_FETCH_LIMIT = 5; // Max number of files to fetch at once
const FETCH_RETRY_ATTEMPTS = 3;
const FETCH_RETRY_DELAY = 1000; // 1 second

/**
 * Get current date in YYYY-MM-DD format
 */
const getCurrentDate = () => {
  return new Date().toISOString().split("T")[0];
};

/**
 * Fetch a file with retry logic
 */
const fetchFileWithRetry = async (fileUrl, retries = FETCH_RETRY_ATTEMPTS) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(
        `📥 Fetching file (attempt ${attempt}/${retries}): ${fileUrl}`,
      );
      const response = await fetch(fileUrl);
      if (response.ok) {
        const blob = await response.blob();
        console.log(
          `✅ Successfully fetched file: ${fileUrl} (${blob.size} bytes)`,
        );
        return blob;
      }
      const errorMsg = `HTTP ${response.status}${response.status === 404 ? " - File not found" : ""}`;
      throw new Error(errorMsg);
    } catch (error) {
      console.warn(
        `⚠️ Attempt ${attempt} failed for ${fileUrl}:`,
        error.message,
      );
      if (attempt === retries) {
        console.error(
          `❌ Failed to fetch file after ${retries} attempts: ${fileUrl}`,
        );
        throw error;
      }
      // Wait before retrying
      await new Promise((resolve) =>
        setTimeout(resolve, FETCH_RETRY_DELAY * attempt),
      );
    }
  }
};

/**
 * Fetch files in batches to limit concurrent connections
 */
const fetchFilesInBatches = async (filesToFetch) => {
  const results = [];

  // Process files in chunks
  for (let i = 0; i < filesToFetch.length; i += CONCURRENT_FETCH_LIMIT) {
    const batch = filesToFetch.slice(i, i + CONCURRENT_FETCH_LIMIT);

    // Fetch all files in this batch concurrently
    const batchPromises = batch.map(async (fileInfo) => {
      try {
        console.log(`Fetching ${fileInfo.originalFilename}...`);
        const blob = await fetchFileWithRetry(fileInfo.fileUrl);
        return {
          ...fileInfo,
          blob,
          success: true,
        };
      } catch (error) {
        console.error(`Failed to fetch ${fileInfo.originalFilename}:`, error);
        return {
          ...fileInfo,
          blob: null,
          success: false,
          error: error.message,
        };
      }
    });

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
  }

  return results;
};

/**
 * Generate new filename based on file type and design
 */
const generateNewFilename = (
  originalFilename,
  designName,
  projectName,
  fileType,
  dateToUse = null,
) => {
  const currentDate = dateToUse || getCurrentDate();
  const extension = originalFilename.split(".").pop()?.toLowerCase() || "";

  // Handle different file types with specific naming conventions
  switch (fileType) {
    case "topcon_files":
      return `${projectName} ${currentDate}.${extension}`;

    case "leica_files":
      return `${currentDate} ${designName}.${extension}`;

    case "vcl_files":
      return `${currentDate} ${designName} VCL.${extension}`;

    case "dxf_ttm_files":
    case "svd_svl_files":
      return `${currentDate} ${designName}.${extension}`;

    case "control_csv_files":
      return "control.office";

    default:
      return originalFilename; // Keep original for unknown types
  }
};

/**
 * Process files and create zip blob efficiently (fetch files only once)
 */
const processFilesAndCreateZip = async (
  designFilesData,
  projectName = "Export",
  dateToUse = null,
) => {
  const zip = new JSZip();
  const currentDate = dateToUse || getCurrentDate();
  const filesToFetch = [];

  // Track which brand folders we need
  let needsTopcon = false;
  let needsLeica = false;
  let needsTrimble = false;
  let needsMachineControl = false; // Only for SVD/SVL files

  // Prepare list of files to fetch
  for (const [designName, designData] of Object.entries(designFilesData)) {
    if (
      designName === "background_images" ||
      designName === "control_csv" ||
      designName === "tbc"
    )
      continue;

    if (!designData || !designData.files) continue;

    const { files } = designData;

    for (const [fileType, fileArray] of Object.entries(files)) {
      if (!Array.isArray(fileArray) || fileArray.length === 0) continue;

      for (const file of fileArray) {
        const originalFilename =
          file.filename || file.original_filename || `file_${Date.now()}`;
        const fileUrl = file.url || file.publicUrl || file.public_url;

        if (fileUrl) {
          const newFilename = generateNewFilename(
            originalFilename,
            designName,
            projectName,
            fileType,
            currentDate,
          );

          // Track which brands we need
          if (fileType === "topcon_files") {
            needsTopcon = true;
          } else if (fileType === "leica_files") {
            needsLeica = true;
          } else if (
            ["vcl_files", "dxf_ttm_files", "svd_svl_files"].includes(fileType)
          ) {
            needsTrimble = true;
            // Machine Control Data folder only needed for SVD/SVL files
            if (fileType === "svd_svl_files") {
              needsMachineControl = true;
            }
          }

          filesToFetch.push({
            originalFilename,
            fileUrl,
            newFilename,
            fileType,
            designName,
          });
        }
      }
    }
  }

  // Handle background images
  let backgroundFiles = [];
  if (designFilesData.background_images) {
    if (Array.isArray(designFilesData.background_images)) {
      backgroundFiles = designFilesData.background_images;
    } else if (
      designFilesData.background_images.files &&
      designFilesData.background_images.files.background_image_files
    ) {
      backgroundFiles =
        designFilesData.background_images.files.background_image_files;
    }
  }

  for (const file of backgroundFiles) {
    const originalFilename =
      file.filename || file.original_filename || `background_${Date.now()}`;
    const fileUrl = file.url || file.publicUrl || file.public_url;

    if (fileUrl) {
      needsTrimble = true; // Background images go in Trimble folder
      filesToFetch.push({
        originalFilename,
        fileUrl,
        newFilename: originalFilename,
        fileType: "background_image_files",
        designName: "background_images",
      });
    }
  }

  // Handle control CSV files (project-level, goes in project root folder)
  let controlCsvFiles = [];
  if (designFilesData.control_csv) {
    if (Array.isArray(designFilesData.control_csv)) {
      controlCsvFiles = designFilesData.control_csv;
    } else if (
      designFilesData.control_csv.files &&
      designFilesData.control_csv.files.control_csv_files
    ) {
      controlCsvFiles = designFilesData.control_csv.files.control_csv_files;
    }
  }

  for (const file of controlCsvFiles) {
    const originalFilename =
      file.filename || file.original_filename || `control_${Date.now()}.csv`;
    const fileUrl = file.url || file.publicUrl || file.public_url;

    if (fileUrl) {
      needsTrimble = true;
      filesToFetch.push({
        originalFilename,
        fileUrl,
        newFilename: "control.office",
        fileType: "control_csv_files",
        designName: "control_csv",
      });
    }
  }

  // Handle TBC files (project-level, goes in Trimble/TBC Files/)
  let tbcFilesList = [];
  if (designFilesData.tbc) {
    if (Array.isArray(designFilesData.tbc)) {
      tbcFilesList = designFilesData.tbc;
    } else if (
      designFilesData.tbc.files &&
      designFilesData.tbc.files.tbc_files
    ) {
      tbcFilesList = designFilesData.tbc.files.tbc_files;
    }
  }

  let needsTbc = false;
  for (const file of tbcFilesList) {
    const originalFilename =
      file.filename || file.original_filename || `tbc_${Date.now()}`;
    const fileUrl = file.url || file.publicUrl || file.public_url;

    if (fileUrl) {
      needsTrimble = true;
      needsTbc = true;
      filesToFetch.push({
        originalFilename,
        fileUrl,
        newFilename: originalFilename,
        fileType: "tbc_files",
        designName: "tbc",
      });
    }
  }

  console.log(
    `Fetching ${filesToFetch.length} files with connection limit of ${CONCURRENT_FETCH_LIMIT}...`,
  );

  // Fetch all files with connection pooling
  const fetchedFiles = await fetchFilesInBatches(filesToFetch);

  // Create folder structure based on what we need
  let topconFolder = null;
  let leicaFolder = null;
  let trimbleFolder = null;
  let projectFolder = null;
  let designsFolder = null;
  let backgroundFolder = null;
  let machineControlFolder = null;
  let allFolder = null;
  let tbcFolder = null;

  if (needsTopcon) {
    topconFolder = zip.folder("TOPCON");
  }
  if (needsLeica) {
    leicaFolder = zip.folder("LEICA");
  }
  if (needsTrimble) {
    trimbleFolder = zip.folder("Trimble");
    projectFolder = trimbleFolder.folder(projectName);
    projectFolder.folder("work orders");
    designsFolder = projectFolder.folder("designs");
    backgroundFolder = projectFolder.folder("Background Images");

    // Only create Machine Control Data folder if we have SVD/SVL files
    if (needsMachineControl) {
      machineControlFolder = trimbleFolder.folder("Machine Control Data");
      allFolder = machineControlFolder.folder("All");
    }

    // Only create TBC Files folder if we have TBC files
    if (needsTbc) {
      tbcFolder = trimbleFolder.folder("TBC Files");
    }
  }

  // Add successfully fetched files to zip
  const downloadedFiles = [];
  for (const fileData of fetchedFiles) {
    if (fileData.success && fileData.blob) {
      let targetFolder;
      let folderPath;

      // Determine target folder based on file type
      if (fileData.fileType === "topcon_files") {
        targetFolder = topconFolder;
        folderPath = "TOPCON";
      } else if (fileData.fileType === "leica_files") {
        targetFolder = leicaFolder;
        folderPath = "LEICA";
      } else if (fileData.fileType === "vcl_files") {
        const vclFolderName =
          `${currentDate} ${fileData.designName} VCL`.trim();
        targetFolder = designsFolder.folder(vclFolderName);
        folderPath = `Trimble/${projectName}/designs/${vclFolderName}`;
      } else if (fileData.fileType === "dxf_ttm_files") {
        const designFolderName = `${currentDate} ${fileData.designName}`.trim();
        targetFolder = designsFolder.folder(designFolderName);
        folderPath = `Trimble/${projectName}/designs/${designFolderName}`;
      } else if (fileData.fileType === "background_image_files") {
        targetFolder = backgroundFolder;
        folderPath = `Trimble/${projectName}/Background Images`;
      } else if (fileData.fileType === "control_csv_files") {
        targetFolder = projectFolder;
        folderPath = `Trimble/${projectName}`;
      } else if (fileData.fileType === "tbc_files") {
        targetFolder = tbcFolder;
        folderPath = `Trimble/TBC Files`;
      } else if (fileData.fileType === "svd_svl_files") {
        const svdFolderName =
          `${projectName} ${currentDate} ${fileData.designName}`.trim();
        targetFolder = allFolder.folder(svdFolderName);
        folderPath = `Trimble/Machine Control Data/All/${svdFolderName}`;
      } else {
        // Default folder for unknown types
        const folderName = fileData.fileType
          .replace(/_files$/, "")
          .replace("_", " ")
          .toUpperCase();
        targetFolder = zip.folder(folderName);
        folderPath = folderName;
      }

      if (targetFolder) {
        targetFolder.file(fileData.newFilename, fileData.blob);
        downloadedFiles.push({
          fileName: fileData.newFilename,
          originalName: fileData.originalFilename,
          folderName: folderPath,
          success: true,
        });
      }
    } else {
      downloadedFiles.push({
        fileName: fileData.originalFilename,
        folderName: "Failed",
        success: false,
        error: fileData.error,
      });
    }
  }

  // Generate zip blob
  console.log("Generating zip file...");
  const zipBlob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: {
      level: 6,
    },
  });

  return {
    zipBlob,
    downloadedFiles,
    totalFiles: downloadedFiles.length,
    successfulDownloads: downloadedFiles.filter((f) => f.success).length,
  };
};

export const createZipDownload = async (
  designFilesData,
  projectName = "Export",
  dateToUse = null,
) => {
  try {
    // Use the new efficient function
    const { zipBlob, downloadedFiles, totalFiles, successfulDownloads } =
      await processFilesAndCreateZip(designFilesData, projectName, dateToUse);

    // Create download link
    const url = URL.createObjectURL(zipBlob);
    const dateStamp = dateToUse || new Date().toISOString().split("T")[0];
    const timeStamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .split("T")[1]
      .split("Z")[0];
    const filename = `${projectName}_${dateStamp}_${timeStamp}.zip`;

    // Trigger download
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    return {
      success: true,
      filename,
      downloadedFiles,
      totalFiles,
      successfulDownloads,
    };
  } catch (error) {
    console.error("Error creating zip download:", error);
    return {
      success: false,
      error: error.message,
      downloadedFiles: [],
      totalFiles: 0,
      successfulDownloads: 0,
    };
  }
};

export const generateBulkDownloadUrl = async (
  fileTypesWithFiles,
  projectName = "Export",
) => {
  try {
    const zip = new JSZip();

    // Initialize folders as null - will be created only when needed
    let trimbleFolder = null;
    let projectFolder = null;
    let designsFolder = null;
    let backgroundFolder = null;
    let machineControlFolder = null;
    let allFolder = null;
    let topconFolder = null;
    let leicaFolder = null;
    let needsMachineControl = false; // Track if we need Machine Control Data folder

    // Process each file type
    for (const [key, files] of fileTypesWithFiles) {
      for (const file of files) {
        const fileName =
          file.filename || file.original_filename || `file_${Date.now()}`;
        const fileUrl = file.url || file.publicUrl || file.public_url;

        if (fileUrl) {
          try {
            const response = await fetch(fileUrl);
            if (response.ok) {
              const blob = await response.blob();
              let targetFolder;

              if (key === "topcon_files") {
                // Create TOPCON folder only when needed
                if (!topconFolder) {
                  topconFolder = zip.folder("TOPCON");
                }
                targetFolder = topconFolder;
              } else if (key === "leica_files") {
                // Create LEICA folder only when needed
                if (!leicaFolder) {
                  leicaFolder = zip.folder("LEICA");
                }
                targetFolder = leicaFolder;
              } else if (
                [
                  "vcl_files",
                  "dxf_ttm_files",
                  "svd_svl_files",
                  "background_image_files",
                ].includes(key)
              ) {
                // Track if we need Machine Control Data folder (only for SVD/SVL)
                if (key === "svd_svl_files" && !needsMachineControl) {
                  needsMachineControl = true;
                }

                // Create Trimble structure only when needed
                if (!trimbleFolder) {
                  trimbleFolder = zip.folder("Trimble");
                  projectFolder = trimbleFolder.folder(projectName);
                  projectFolder.folder("work orders");
                  designsFolder = projectFolder.folder("designs");
                  backgroundFolder = projectFolder.folder("Background Images");
                }

                // Only create Machine Control Data folder if we have SVD/SVL files
                if (needsMachineControl && !machineControlFolder) {
                  machineControlFolder = trimbleFolder.folder(
                    "Machine Control Data",
                  );
                  allFolder = machineControlFolder.folder("All");
                }

                const currentDate = getCurrentDate();
                // Extract potential design name from filename (fallback if no specific design provided)
                const designName = fileName.split("_")[0] || "Design";

                if (key === "vcl_files") {
                  const vclFolderName =
                    `${currentDate} ${designName} VCL`.trim();
                  targetFolder = designsFolder.folder(vclFolderName);
                } else if (key === "dxf_ttm_files") {
                  const designFolderName =
                    `${currentDate} ${designName}`.trim();
                  targetFolder = designsFolder.folder(designFolderName);
                } else if (key === "background_image_files") {
                  targetFolder = backgroundFolder;
                } else if (key === "svd_svl_files") {
                  const svdFolderName =
                    `${projectName} ${currentDate} ${designName}`.trim();
                  targetFolder = allFolder.folder(svdFolderName);
                }
              } else {
                // Other file types - create folder with uppercase name
                const folderName = key
                  .replace("_files", "")
                  .replace("_", " ")
                  .toUpperCase();
                targetFolder = zip.folder(folderName);
              }

              targetFolder.file(fileName, blob);
            }
          } catch (error) {
            console.warn(`Failed to add ${fileName} to zip:`, error);
          }
        }
      }
    }

    // Generate zip blob
    const zipBlob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: {
        level: 6,
      },
    });

    // Create blob URL
    const url = URL.createObjectURL(zipBlob);
    const dateStamp = new Date().toISOString().split("T")[0];
    const timeStamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .split("T")[1]
      .split("Z")[0];
    const filename = `${projectName}_${dateStamp}_${timeStamp}.zip`;

    return {
      success: true,
      url,
      filename,
      cleanup: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    console.error("Error generating bulk download URL:", error);
    return {
      success: false,
      error: error.message,
      url: null,
      filename: null,
      cleanup: () => {},
    };
  }
};

// Create zip with proper renaming and upload to storage for public URL
export const createAndUploadZip = async (
  designFilesData,
  projectName = "Export",
  projectId = "default",
  clientId = "default",
  exportDate = null,
) => {
  const startTime = Date.now();
  console.log(`🎯 Starting createAndUploadZip for project: ${projectName}`);
  console.log(`🎯 Project ID: ${projectId}, Client ID: ${clientId}`);

  try {
    // Use the new efficient function to get zip blob without downloading
    console.log(
      `📦 Creating zip file with design data:`,
      Object.keys(designFilesData),
    );
    const { zipBlob, downloadedFiles, totalFiles, successfulDownloads } =
      await processFilesAndCreateZip(designFilesData, projectName, exportDate);

    console.log(`📦 Zip creation complete:`, {
      totalFiles,
      successfulDownloads,
      failedFiles: totalFiles - successfulDownloads,
      zipSize: zipBlob.size,
    });

    // Check if we have any successful downloads
    if (successfulDownloads === 0) {
      console.error(`❌ No files were successfully downloaded`);
      return {
        success: false,
        error: `Failed to download any files. All ${totalFiles} files failed to fetch.`,
        downloadedFiles: downloadedFiles,
        totalFiles: totalFiles,
        successfulDownloads: 0,
      };
    }

    // Log warning if some files failed
    if (successfulDownloads < totalFiles) {
      console.warn(
        `⚠️ Only ${successfulDownloads} of ${totalFiles} files were successfully downloaded`,
      );
      const failedFiles = downloadedFiles.filter((f) => !f.success);
      console.warn(`Failed files:`, failedFiles);
    }

    // Upload to storage
    console.log(`☁️ Uploading zip to Supabase storage...`);
    const { SupabaseStorageService } =
      await import("../services/supabaseStorageService");
    // Use provided exportDate for the base, but add unique timestamp to prevent collisions
    const dateStamp = exportDate || new Date().toISOString().split("T")[0];
    const timeStamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .split("T")[1]
      .split("Z")[0];
    const filename = `${projectName}_${dateStamp}_${timeStamp}.zip`;

    const uploadResult = await SupabaseStorageService.uploadZipToStorage(
      zipBlob,
      filename,
      projectId,
      clientId,
    );

    if (uploadResult.success) {
      const elapsed = Date.now() - startTime;
      console.log(`✅ Successfully created and uploaded zip in ${elapsed}ms`);
      console.log(`✅ Download URL: ${uploadResult.url}`);

      return {
        success: true,
        url: uploadResult.url,
        filename: uploadResult.filename,
        filePath: uploadResult.filePath,
        size: uploadResult.size,
        downloadedFiles: downloadedFiles,
        totalFiles: totalFiles,
        successfulDownloads: successfulDownloads,
      };
    } else {
      console.error(`❌ Failed to upload zip to storage:`, uploadResult.error);
      return {
        success: false,
        error: `Failed to upload zip: ${uploadResult.error}`,
        downloadedFiles: downloadedFiles,
        totalFiles: totalFiles,
        successfulDownloads: successfulDownloads,
      };
    }
  } catch (error) {
    console.error("❌ Error creating and uploading zip:", error);
    console.error("Error stack:", error.stack);
    return {
      success: false,
      error: error.message,
      downloadedFiles: [],
      totalFiles: 0,
      successfulDownloads: 0,
    };
  }
};

const zipDownloadUtils = {
  createZipDownload,
  generateBulkDownloadUrl,
  createAndUploadZip,
};

export default zipDownloadUtils;
